import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Ticket, Award, Crown, Gem, Sparkles, AlertCircle, Star, Building2, Landmark, ArrowRight, MessageCircle } from 'lucide-react';
export const TicketsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const ticketOptions = [{
    title: 'Darbar Pass',
    subtitle: 'Standing',
    features: ['Standing-room access to immersive experience', 'Late entry', 'Access to chai & sweets lounge', 'Photo moments throughout the venue', 'Digital souvenir collection'],
    limitations: ['Standing-room only', 'No reserved viewing area'],
    popular: false,
    icon: <Ticket className="w-8 h-8" data-id="element-1902" data-mp="37dmf5yx0w69e" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Council',
    subtitle: 'Full Experience',
    features: ['Admission to immersive experience', 'Curated drinks & scent journey', 'Digital souvenir collection', 'Guided historical journey', 'VIP sensory experience', 'Behind-the-scenes tour'],
    popular: true,
    icon: <Award className="w-8 h-8" data-id="element-1903" data-mp="2no3owzomms39" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Founder Circle',
    subtitle: 'Limited Availability',
    features: ['All Council features', <span key="turban" data-id="element-1904" data-mp="2jns2pigm8jpo">
          <strong data-id="element-1905" data-mp="2jp37nllsrxid">Personalized royal turban experience</strong> — wear an
          authentic turban designed and inspired by the Sikh Kingdom era
        </span>, <span key="coin" data-id="element-1906" data-mp="hwoq4nn60v2h">
          <strong data-id="element-1907" data-mp="hwyighvonebc">Exclusive Maharaja Ranjit Singh Coin</strong>
        </span>, <span key="book" data-id="element-1908" data-mp="fhs14992q5rf">
          <strong data-id="element-1909" data-mp="fia37mif3e79">Autographed pre-release copy of House of Lions</strong> —
          personally signed by author A.K. Singh
        </span>, 'Early prototype invite', 'Recognition as Founding Member', 'Private meet with creators', 'Custom historical portrait'],
    popular: false,
    icon: <Crown className="w-8 h-8" data-id="element-1910" data-mp="1jtop9ebcjek" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Patron Circle',
    subtitle: 'Table for 10',
    features: ['All Founder Circle features', 'Private hosted table for 10 guests', <span key="likeness-patron" data-id="element-1911" data-mp="pewogceno79t">
          <strong data-id="element-1912" data-mp="pdv2ve00k2du">Your likeness featured in the exhibit</strong>
        </span>, 'Lifetime recognition as a major supporter', 'Exclusive pre-event reception', 'Input into future cultural experiences'],
    popular: false,
    icon: <Gem className="w-8 h-8" data-id="element-1913" data-mp="1d50grnme8kli" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }];
  return <motion.section id="tickets" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1914" data-mp="1kj1clm0rnwc8">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1915" data-mp="1uebbw1zs5pmb"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1916" data-mp="1zez58ubheijp">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1917" data-mp="23gkl8ojbbcy4">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1918" data-mp="2i6jk6tioysen">
            Reserve Your Place
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1919" data-mp="2r4vizitpowi6">
            TICKET
            <br data-id="element-1920" data-mp="3ou05c4jw5s1j" />
            <span className="text-amber-400" data-id="element-1921" data-mp="3otgkaeyy2nja">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1922" data-mp="3jv6s8d8io31w"></div>
          <div className="max-w-3xl mx-auto" data-id="element-1923" data-mp="3jt2nxu6ph213">
            <p className="text-xl text-amber-400 font-light mb-4" data-id="element-1924" data-mp="1hpff40xlc0k">
              Prototype Event · Summer 2026
            </p>
            <p className="text-white/70 font-light leading-relaxed mb-6" data-id="element-1925" data-mp="3tm9on1qi3tuu">
              Your ticket includes admission to the exclusive prototype event
              plus one monthly experience of your choice between May and
              December 2026.
            </p>
            <div className="flex justify-between items-center pt-4 border-t border-white/10" data-id="element-1926" data-mp="3ewlquahj3dbv">
              <span className="text-amber-400 font-light tracking-wider" data-id="element-1927" data-mp="39whg713axctk">
                Summer 2026
              </span>
              <span className="text-white/60 text-sm tracking-wider uppercase" data-id="element-1928" data-mp="u4tj95oafkar">
                Prototype Event
              </span>
            </div>
          </div>
        </motion.div>
        {/* Tickets Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-20" data-id="element-1929" data-mp="xm7l3co9s9dl">
          {ticketOptions.map((ticket, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: index * 0.1
        }} className={`relative ${ticket.popular ? 'lg:-mt-4 lg:mb-4' : ''}`} data-id="element-1930" data-mp="nq4jc7xbycr6" data-mpi={index}>
              {/* Popular Badge */}
              {ticket.popular && <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-1931" data-mp="1zdecq2c0jti5">
                  <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2 border-2 border-amber-300" data-id="element-1932" data-mp="2444gjb4sjoaq">
                    <Star className="w-4 h-4" fill="currentColor" data-id="element-1933" data-mp="2dzbdikhdfsu1" />
                    <span className="text-xs uppercase tracking-wider" data-id="element-1934" data-mp="290gvvlv54abi">
                      Most Popular
                    </span>
                  </div>
                </div>}
              {/* Card */}
              <div className="border-2 border-amber-400/30 bg-black/80 h-full flex flex-col" data-id="element-1935" data-mp="1zzabh1pdefcz">
                <div className={`p-8 flex flex-col h-full ${ticket.popular ? 'pt-12' : ''}`} data-id="element-1936" data-mp="1v20nlqhmqbr9">
                  {/* Icon & Title */}
                  <div className="text-center mb-8" data-id="element-1937" data-mp="1law90rvv8swy">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-amber-400/30 bg-black" data-id="element-1938" data-mp="1lbfjxon3mh1h">
                      <div className="text-amber-400" data-id="element-1939" data-mp="1cahmxqb2v9se">{ticket.icon}</div>
                    </div>
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-1940" data-mp="17begq2jd2xiu">
                      {ticket.title}
                    </h3>
                    {ticket.subtitle && <p className="text-amber-400/60 text-sm font-light tracking-wider uppercase" data-id="element-1941" data-mp="3j0ueljq75ok0">
                        {ticket.subtitle}
                      </p>}
                    <div className="w-16 h-px bg-amber-400/30 mx-auto mt-4" data-id="element-1942" data-mp="3s07q0hxvsn7d"></div>
                  </div>
                  {/* Features */}
                  <ul className="mb-8 flex-grow space-y-3" data-id="element-1943" data-mp="308ihbbeehdgq">
                    {ticket.features.map((feature, i) => <li key={i} className="text-white/70 text-sm flex items-start font-light" data-id="element-1944" data-mp="34929e0k49qw0" data-mpi={i}>
                        <span className="flex-shrink-0 mr-3 mt-1.5" data-id="element-1945" data-mp="3dznb3wv37bj5">
                          <span className="block w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.5)]" data-id="element-1946" data-mp="3e0yevwoj8ja9"></span>
                        </span>
                        <span data-id="element-1947" data-mp="2liavclp6pcba">{feature}</span>
                      </li>)}
                  </ul>
                  {/* Limitations */}
                  {ticket.limitations && <div className="mb-6 border border-amber-400/20 p-4 bg-black/40" data-id="element-1948" data-mp="19xywz4te0v99">
                      <div className="flex items-center mb-2" data-id="element-1949" data-mp="1nwykua92t64d">
                        <AlertCircle className="w-4 h-4 text-amber-400 mr-2" data-id="element-1950" data-mp="1nudhfgt4qi4i" />
                        <span className="text-amber-400 text-xs uppercase tracking-wider" data-id="element-1951" data-mp="1jttjib0qfahb">
                          Limitations
                        </span>
                      </div>
                      <ul className="space-y-1" data-id="element-1952" data-mp="1sv9sqfft96jy">
                        {ticket.limitations.map((limitation, i) => <li key={i} className="text-white/50 text-xs flex items-start font-light" data-id="element-1953" data-mp="27ms41zxq6cwz" data-mpi={i}>
                            <span className="mr-2" data-id="element-1954" data-mp="2glnddkw2g34i">•</span>
                            <span data-id="element-1955" data-mp="2bn2bzi52vdeh">{limitation}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {/* CTA Button */}
                  <motion.a href={ticket.link} target="_blank" rel="noopener noreferrer" className={`w-full py-3 text-center transition-colors duration-300 ${ticket.popular ? 'bg-amber-400 hover:bg-amber-500 text-black font-medium' : 'border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white'}`} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-1956" data-mp="30649uv5tksly">
                    <span className="flex items-center justify-center gap-2 text-sm tracking-wider uppercase" data-id="element-1957" data-mp="27jjm04ojyyn5">
                      {ticket.popular && <Sparkles className="w-4 h-4" data-id="element-1958" data-mp="1tjij6joq9h51" />}
                      Buy Tickets Now
                    </span>
                  </motion.a>
                </div>
              </div>
            </motion.div>)}
        </div>
        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto" data-id="element-1959" data-mp="1dpp9ip3qqk27">
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-1960" data-mp="1wf488cvyex3f">
            History will only be reborn{' '}
            <span className="text-amber-400" data-id="element-1961" data-mp="1io8uonaifrti">once</span>. Will you be there?
          </h3>
          <p className="text-xl text-white/70 mb-8 font-light" data-id="element-1962" data-mp="1nmk4wz4pw4c9">
            Tickets are selling fast. Reserve your place in history today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-1963" data-mp="2bb3erj3f0p6g">
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1964" data-mp="2g3wkoaame3k4">
              Buy Tickets Now
            </motion.a>
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="border-2 border-amber-400/50 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1965" data-mp="26l5ag8llgqu0">
              View All Ticket Options
            </motion.a>
          </div>
        </motion.div>
        {/* Private & Partnership Events Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-6xl mx-auto mt-20" data-id="element-1966" data-mp="2swfcriyztwj">
          <div className="text-center mb-12" data-id="element-1967" data-mp="3g8ufh59iag81">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-4" data-id="element-1968" data-mp="3g9ndnhuefxek">
              Exclusive Partnerships
            </p>
            <h3 className="font-serif text-4xl md:text-5xl text-white mb-4" data-id="element-1969" data-mp="3pyx5qxp8tg2o">
              PRIVATE &amp; <span className="text-amber-400" data-id="element-1970" data-mp="3q0qhusimksi2">PARTNERSHIP</span>{' '}
              EVENTS
            </h3>
            <div className="w-24 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-1971" data-mp="2ximgmzcuabxn"></div>
            <p className="text-white/70 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-1972" data-mp="2xgrszda6o5pv">
              Transform the Sikh Kingdoms experience into a bespoke event for
              your organization, community, or constituency. We offer tailored
              partnerships for corporate sponsors, creators, and community
              leaders.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1973" data-mp="2b11cx64fngkl">
            {/* Corporate Sponsors */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} data-id="element-1974" data-mp="271sh7vpu18me">
              <Link to="/corporate" className="group block h-full" data-id="element-1975" data-mp="1nbqf9xidq2ao">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-1976" data-mp="1jaesdzqw3v66">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-1977" data-mp="2d0e00h55r78i"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-1978" data-mp="1z8qvju12asen">
                    <Building2 className="w-6 h-6 text-amber-400" data-id="element-1979" data-mp="1p9rt4t6w3t03" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-1980" data-mp="1ua62cek898zp">
                    Brand Partnership &amp; Sponsorship
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-1981" data-mp="1kb6tauvjvbc6">
                    Corporate Sponsors
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-1982" data-mp="3ephk38vpgfkl">
                    Transform your next corporate gathering into an
                    unforgettable cultural experience. Host your team, clients,
                    or partners for an exclusive private evening in the Sikh
                    Kingdoms.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-1983" data-mp="1ctiid2chbfq9">
                    <span data-id="element-1984" data-mp="17znwptdbcqoj">Explore Partnership</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1985" data-mp="1mpc2b6sj2bi2" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Influencers & Creators */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-1986" data-mp="1vop91rp9olz3">
              <Link to="/influencers" className="group block h-full" data-id="element-1987" data-mp="21fa4he9vuny6">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-1988" data-mp="21fjuh1uxgv3v">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-1989" data-mp="2ah16o957scna"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-1990" data-mp="2kdw5yu9bah2m">
                    <Sparkles className="w-6 h-6 text-amber-400" data-id="element-1991" data-mp="2p6z1v91ka2lz" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-1992" data-mp="2u35wi5oibrom">
                    Host an Exclusive Experience
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-1993" data-mp="335ovuft33m64">
                    Influencers &amp; Creators
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-1994" data-mp="mh5ydtp1fh4d">
                    Create extraordinary content at the intersection of culture,
                    cuisine, and immersive art. Bring your audience into a world
                    of 360° projections and royal Punjabi heritage.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-1995" data-mp="1a9mdbvdhaszg">
                    <span data-id="element-1996" data-mp="11fhpzlxjzzoo">Explore Hosting</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1997" data-mp="11e6mzobxmvym" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Politicians & Community Leaders */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1998" data-mp="1p5c1o064zi7c">
              <Link to="/politicians" className="group block h-full" data-id="element-1999" data-mp="si4redj3736d">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-2000" data-mp="sg1ud9jio9s5">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-2001" data-mp="1l6juc1oy1v1c"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-2002" data-mp="1b8cq8jfr77gr">
                    <Landmark className="w-6 h-6 text-amber-400" data-id="element-2003" data-mp="3rzvzhrrcujq4" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-2004" data-mp="3vws49zi7z6z4">
                    Constituency &amp; Cultural Night
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-2005" data-mp="3jg98z0lj9eqz">
                    Elected Officials &amp; Community Leaders
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-2006" data-mp="3tclzxqpp1ei1">
                    Host a nonpartisan constituency night that celebrates
                    heritage, education, and multicultural excellence — a
                    community economic win for your riding.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-2007" data-mp="3ao7cteihykmi">
                    <span data-id="element-2008" data-mp="2mycwuo2vsfy1">Explore Constituency Night</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-2009" data-mp="2mws1wxcnmuzn" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* General Inquiry CTA */}
          <div className="text-center" data-id="element-2010" data-mp="3v8lzy7tfle8f">
            <p className="text-white/50 text-sm font-light mb-4" data-id="element-2011" data-mp="3v5gausksyccp">
              Not sure which option is right for you?
            </p>
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20a%20private%20event%20or%20partnership%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 border-2 border-amber-400/50 hover:border-amber-400 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-2012" data-mp="82bieaqdnev0">
              <MessageCircle className="w-4 h-4" data-id="element-2013" data-mp="2tk0doh572s6d" />
              General Partnership Inquiry
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.section>;
};